import React, {Component, ReactNode} from 'react'
import FacilitiesRepository from '../facilities/FacilitiesRepository'
import ReservationsRepository from './ReservationsRepository'
import EventEmitterExResident from '../shared/pageBaseComponents/EventEmitterExResident'
import {createResidentPage, ResidentPagePropsSet} from '../shared/hoc/createResidentPage'
import {Nullable} from '../../app/TypeHelpers'
import {NatAsyncCallFunction} from '../../__NoAutomatedTests/common/util/Natutil'
import moment from 'moment'
import {convertToNullableMoment} from './helpers/helpers'
import {DisplayItemDividerForPageTop} from '../../styledComponents/DisplayItem'
import {
    DisplayItemCostInformationContainer,
    DisplayItemCostPaymentTypeAndSubtotalContainer,
    DisplayItemPaymentType,
    DisplayItemSubtotalCost,
    DisplayItemTotalCostContainer,
    DisplayItemTotalCostHeader,
    DisplayItemTotalCostValue,
} from './ResidentReserveFacilityFormComponents/RRFFReservePaymentView'
import {PAYMENT_TYPE, RESERVATION_STATUS} from '../../CodeMaster'
import {formatNumberWithCommas} from '../../formatters/formatNumberWithCommas'
import {PlainBackLink} from '../../styledComponents/Links'
import {BlockSpacer} from '../../styledComponents/ResponsiveConvenience'
import {StyledPageTitle} from './components/CommonStyle'
import ReservationDetailItem from './components/ReservationDetailItem'
import {RowFlexContainer} from '../../styledComponents/FlexComponents'
import ReservationStatusTag from './components/ReservationStatusTag'
import {Location} from 'react-router-dom'

interface LocationState {
    previousPageUrl: string
}

interface Props {
    condominiumCd: string
    reserveId: number
    facilitiesRepo: FacilitiesRepository
    reservationsRepo: ReservationsRepository
    eventEmitter: EventEmitterExResident

    location: Location<LocationState>
}

interface State {
    facilityName: Nullable<string>
    reserveTitle: Nullable<string>
    reserveDate: Nullable<string>
    charge: Nullable<number>
    paymentType: Nullable<string>
}

export interface AdditionalChargeDate {
    facilityName: Nullable<string>
    reserveTitle: Nullable<string>
    reserveDate: string
    charge: number
    paymentType: string
}

class ResidentReserveExtraReservationPage extends Component<Props & ResidentPagePropsSet, State> {

    constructor(props: Readonly<Props & ResidentPagePropsSet>) {
        super(props)
        this.state = {
            facilityName: null,
            reserveTitle: null,
            reserveDate: null,
            charge: null,
            paymentType: null,
        }
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        const {condominiumCd, reserveId, reservationsRepo, navigate} = this.props
        const additionalCharge: Nullable<AdditionalChargeDate> =
            await NatAsyncCallFunction(
                reservationsRepo.getAdditionalCharge.bind(
                    reservationsRepo,
                    condominiumCd,
                    reserveId,
                ), navigate)

        if (additionalCharge !== null) {
            this.setState({
                facilityName: additionalCharge.facilityName,
                reserveTitle: additionalCharge.reserveTitle,
                reserveDate: additionalCharge.reserveDate,
                charge: additionalCharge.charge,
                paymentType: additionalCharge.paymentType,
            })
        }

        this.props.eventEmitter.closeLoading()
    }

    render(): ReactNode {
        const {condominiumCd} = this.props
        const {facilityName, reserveDate, reserveTitle, charge, paymentType} = this.state

        const reserveDateMoment: Nullable<moment.Moment> = convertToNullableMoment(reserveDate, null)
        let reserveDateString: string = ''
        if (reserveDateMoment !== null) {
            reserveDateString = reserveDateMoment.format('YYYY年M月D日(ddd)')
        }

        let previousPageUrl: string = `/resident/condominiums/${condominiumCd}/reserve-facility-top`
        if (this.props.location.state) {
            previousPageUrl = this.props.location.state.previousPageUrl
        }

        return (
            <>
                <PlainBackLink to={previousPageUrl}>戻る</PlainBackLink>
                <BlockSpacer height={'15px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <RowFlexContainer>
                    <StyledPageTitle>予約詳細</StyledPageTitle>
                    <ReservationStatusTag
                        reservationStatus={RESERVATION_STATUS.ELECTION}
                    />
                </RowFlexContainer>
                <BlockSpacer height={'10px'}/>
                {
                    (facilityName !== null) &&
                    <>
                        <BlockSpacer height={'5px'}/>
                        <ReservationDetailItem
                            type={'texts'}
                            title={'施設名'}
                            texts={[facilityName]}
                        />
                    </>
                }

                <BlockSpacer height={'5px'}/>
                <ReservationDetailItem
                    type={'texts'}
                    title={'利用日'}
                    texts={[reserveDateString]}
                />

                {
                    (reserveTitle && reserveTitle !== '') &&
                    <>
                        <BlockSpacer height={'5px'}/>
                        <ReservationDetailItem
                            type={'texts'} title={'予約タイトル'}
                            texts={[reserveTitle]}
                        />
                    </>
                }

                <BlockSpacer height={'5px'}/>
                <ReservationDetailItem
                    type={'component'} title={'お支払い金額'}
                    component={this.renderPaymentItem(paymentType, charge)}
                />
                <BlockSpacer height={'15px'}/>
            </>
        )
    }

    renderPaymentItem(paymentType: Nullable<string>, charge: Nullable<number>): ReactNode {
        return <>
            <BlockSpacer height={'10px'}/>
            <DisplayItemCostInformationContainer>
                <DisplayItemCostPaymentTypeAndSubtotalContainer>
                    {
                        (paymentType !== null) &&
                        <DisplayItemPaymentType>
                            {PAYMENT_TYPE.codeToEnumConstants(paymentType).label}
                        </DisplayItemPaymentType>
                    }
                    <DisplayItemSubtotalCost>
                        {
                            (charge !== null)
                                ? formatNumberWithCommas(charge)
                                : '-'
                        }
                        円
                    </DisplayItemSubtotalCost>
                </DisplayItemCostPaymentTypeAndSubtotalContainer>
            </DisplayItemCostInformationContainer>
            <BlockSpacer height={'20px'}/>
            <DisplayItemTotalCostContainer>
                <DisplayItemTotalCostHeader>合計</DisplayItemTotalCostHeader>
                <DisplayItemTotalCostValue>
                    {
                        (charge !== null)
                            ? formatNumberWithCommas(charge)
                            : '-'
                    }
                    円
                </DisplayItemTotalCostValue>
            </DisplayItemTotalCostContainer>
        </>
    }
}

export default createResidentPage(ResidentReserveExtraReservationPage)
